import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Loader } from "semantic-ui-react";
import { getWhatsAppMessages } from "../../apis/whatsapp";
import ComposeBox from "./ComposeBox";
import toastr from "toastr";
import twilioService from "../../services/twilioService";

function formatMessageTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateDivider(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString([], {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function isSameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

const MessageThread = ({ conversation, messages, onMessagesLoaded }) => {
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isInitialLoad = useRef(true);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0 && isInitialLoad.current) {
      scrollToBottom();
      isInitialLoad.current = false;
      setOldestCursor(messages[0]?.createdAt || null);
    }
  }, [messages, scrollToBottom]);

  useEffect(() => {
    isInitialLoad.current = true;
    setHasOlder(true);
    setOldestCursor(null);
  }, [conversation]);

  const isPrependingRef = useRef(false);

  useEffect(() => {
    if (!isInitialLoad.current && !isPrependingRef.current) {
      scrollToBottom();
    }
    isPrependingRef.current = false;
  }, [messages.length, scrollToBottom]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || !hasOlder || !conversation) return;
    const container = scrollContainerRef.current;
    const prevScrollHeight = container ? container.scrollHeight : 0;

    setLoadingOlder(true);
    try {
      const response = await getWhatsAppMessages(conversation.phoneNumber, oldestCursor);
      const older = response.data.messages || [];

      if (older.length === 0) {
        setHasOlder(false);
      } else {
        const nextCursor = older[0]?.createdAt || null;
        setOldestCursor(nextCursor);
        setHasOlder(older.length >= 50);
        isPrependingRef.current = true;
        onMessagesLoaded((prev) => [...older, ...prev]);

        if (container) {
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
          });
        }
      }
    } catch (err) {
      toastr.error("Failed to load older messages");
    } finally {
      setLoadingOlder(false);
    }
  }, [loadingOlder, hasOlder, conversation, oldestCursor, onMessagesLoaded]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 80 && hasOlder && !loadingOlder) {
      loadOlderMessages();
    }
  }, [hasOlder, loadingOlder, loadOlderMessages]);

  const handleSend = useCallback(
    async (text) => {
      if (!conversation) return;
      setSending(true);
      await twilioService
        .sendWhatsApp(conversation.phoneNumber, text, {
          applicantId: conversation.applicantId,
          zendeskId: conversation.zendeskId,
          product: conversation.product,
        })
        .catch(() => {}) // Error toast handled by twilioService
        .finally(() => setSending(false));
    },
    [conversation],
  );

  if (!conversation) {
    return (
      <div className="wa-message-thread wa-message-thread--empty">
        <div className="wa-message-thread__placeholder">
          Select a conversation to start chatting
        </div>
      </div>
    );
  }

  const displayName = conversation.applicantName || conversation.phoneNumber;

  return (
    <div className="wa-message-thread">
      <div className="wa-message-thread__header">
        <div className="wa-message-thread__header-name">{displayName}</div>
        <div className="wa-message-thread__header-phone">{conversation.phoneNumber}</div>
        {(conversation.zendeskId || conversation.zendeskTicketId) && (
          <div className="wa-message-thread__header-zendesk">
            Zendesk #{conversation.zendeskId || conversation.zendeskTicketId}
          </div>
        )}
      </div>

      <div
        className="wa-message-thread__messages"
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {loadingOlder && (
          <div className="wa-message-thread__load-older">
            <Loader active inline="centered" size="mini" />
          </div>
        )}

        {messages.map((msg, idx) => {
          const prevMsg = messages[idx - 1];
          const showDateDivider =
            !prevMsg || !isSameDay(prevMsg.createdAt, msg.createdAt);
          const directionClass = msg.direction === "outbound" ? "outbound" : "inbound";

          return (
            <React.Fragment key={msg._id || idx}>
              {showDateDivider && (
                <div className="wa-date-divider">
                  <span>{formatDateDivider(msg.createdAt)}</span>
                </div>
              )}
              <div className={`wa-message wa-message--${directionClass}`}>
                <div className="wa-message__bubble">
                  <span className="wa-message__text">{msg.body}</span>
                  <span className="wa-message__time">{formatMessageTime(msg.createdAt)}</span>
                </div>
              </div>
            </React.Fragment>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <ComposeBox onSend={handleSend} disabled={sending} />
    </div>
  );
};

MessageThread.propTypes = {
  conversation: PropTypes.shape({
    phoneNumber: PropTypes.string.isRequired,
    applicantName: PropTypes.string,
    applicantId: PropTypes.string,
    zendeskId: PropTypes.string,
    zendeskTicketId: PropTypes.string,
    product: PropTypes.string,
  }),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      body: PropTypes.string,
      direction: PropTypes.string,
      createdAt: PropTypes.string,
    }),
  ).isRequired,
  onMessagesLoaded: PropTypes.func.isRequired,
};

export default MessageThread;
